import { Outlet } from "react-router-dom";
import { BottomNav } from "./BottomNav";
import { HeaderProvider, useHeaderContext } from "@/contexts/HeaderContext";
import { useSafeArea } from "./SafeAreaProvider";
import { 
  HEADER_CONTENT_HEIGHT, 
  BOTTOMNAV_HEIGHT,
  Z_INDEX,
  CSS_SAFE_AREA
} from "@/lib/layout-constants";

/**
 * AppShell - Master layout controller for iOS Native App via Capacitor
 * 
 * ARQUITETURA iOS NATIVA (OBRIGATÓRIA):
 * 
 * ┌─────────────────────────────────────────────────┐
 * │ Safe Area Top (StatusBar) - SOBERANO           │ ← Preto sólido, fora do layout
 * ├─────────────────────────────────────────────────┤
 * │ Header Social (44px) - FIXO                    │ ← Nunca scrolla
 * ├─────────────────────────────────────────────────┤
 * │                                                 │
 * │ Main Content - ÚNICO scrollável                │ ← overflow-y: auto
 * │                                                 │
 * ├─────────────────────────────────────────────────┤
 * │ Tab Bar (56px) - FIXO                          │ ← Nunca scrolla
 * ├─────────────────────────────────────────────────┤
 * │ Safe Area Bottom (Home Indicator)              │ ← Fundo sólido
 * └─────────────────────────────────────────────────┘
 * 
 * REGRAS:
 * - Container principal é position: fixed com flex column
 * - Body/HTML têm overflow: hidden (sem scroll global)
 * - Apenas <main> tem scroll
 * - Header e BottomNav são flexShrink: 0
 */

const AppShellContent = () => {
  const { headerElement, hideHeader } = useHeaderContext();
  
  // SEMPRE chamar useSafeArea para garantir que --sat/--sab são injetados globalmente
  // Isso garante que páginas full-screen (Territory Mode) tenham acesso às CSS vars corretas
  useSafeArea();
  
  return (
    <div 
      className="app-shell-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // Garantir que o container ocupe toda a tela
        width: '100%',
        height: '100%',
      }}
    >
      {/* ═══════════════════════════════════════════════════════════════
          SAFE AREA TOP (iOS Status Bar)
          - Fundo preto sólido (ou transparente se hideHeader)
          - Altura = env(safe-area-inset-top)
          - flexShrink: 0 = nunca encolhe
          ═══════════════════════════════════════════════════════════════ */}
      {!hideHeader && (
        <div 
          aria-hidden="true"
          style={{
            height: CSS_SAFE_AREA.top,
            minHeight: CSS_SAFE_AREA.top,
            background: '#000000',
            flexShrink: 0,
          }}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════
          HEADER SOCIAL (44px)
          - Fundo sólido (background color)
          - NUNCA scrolla
          - z-index alto para ficar acima do conteúdo
          - OCULTO se hideHeader = true (para Territory Mode)
          ═══════════════════════════════════════════════════════════════ */}
      {!hideHeader && (
        <div 
          data-header-container="true"
          style={{
            height: HEADER_CONTENT_HEIGHT,
            minHeight: HEADER_CONTENT_HEIGHT,
            flexShrink: 0,
            background: 'hsl(var(--background))',
            zIndex: Z_INDEX.header,
            position: 'relative',
          }}
        >
          {headerElement}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          MAIN CONTENT - ÚNICO ELEMENTO SCROLLÁVEL
          - flex: 1 = ocupa todo espaço disponível
          - overflow-y: auto = scroll vertical
          - -webkit-overflow-scrolling: touch = momentum scroll iOS
          ═══════════════════════════════════════════════════════════════ */}
      <main 
        id="app-main-content"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          // Fundo para evitar transparência
          background: 'hsl(var(--background))',
        }}
      >
        <Outlet />
      </main>

      {/* ═══════════════════════════════════════════════════════════════
          TAB BAR / BOTTOM NAV (56px)
          - NUNCA scrolla
          - flexShrink: 0
          - Fundo sólido
          ═══════════════════════════════════════════════════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════
          TAB BAR / BOTTOM NAV (64px + safe-area-bottom integrado)
          - NUNCA scrolla
          - flexShrink: 0
          - Safe area bottom INTEGRADO dentro do BottomNav
          ═══════════════════════════════════════════════════════════════ */}
      <div 
        style={{
          height: `calc(${BOTTOMNAV_HEIGHT}px + ${CSS_SAFE_AREA.bottom})`,
          minHeight: `calc(${BOTTOMNAV_HEIGHT}px + ${CSS_SAFE_AREA.bottom})`,
          flexShrink: 0,
          zIndex: Z_INDEX.bottomNav,
          background: 'hsl(var(--background))',
        }}
      >
        <BottomNav />
      </div>
    </div>
  );
};

export const AppShell = () => (
  <HeaderProvider>
    <AppShellContent />
  </HeaderProvider>
);
